// --
// -- Totals for a table's footer row.
// --
// -- Only ever over the rows on screen. None of these tables holds the whole
// -- result set - Recent Orders is a page, a drill-down is capped - so a footer
// -- built here is a total of what is shown, and says so in its label.
// --

/**
 * Sum of the values that are present.
 *
 * Null when none of them are, so an empty column footers blank rather than
 * claiming a total of zero - the same distinction the cells themselves draw.
 */
export const sumOf = <T,>(rows: T[], pick: (row: T) => number | null | undefined): number | null => {
  let total = 0;
  let seen = false;

  rows.forEach((row) => {
    const value = pick(row);
    if (value === null || value === undefined || Number.isNaN(value)) return;
    total += value;
    seen = true;
  });

  return seen ? total : null;
};

/**
 * The mean over a set of group averages, weighted by each group's size.
 *
 * What an average column's footer has to be: adding averages together gives a
 * number that means nothing, and averaging them unweighted lets a group of one
 * order count for as much as a group of a thousand.
 */
export const weightedMean = <T,>(
  rows: T[],
  pick: (row: T) => number | null | undefined,
  weigh: (row: T) => number | null | undefined,
): number | null => {
  let weighted = 0;
  let weight = 0;

  rows.forEach((row) => {
    const value = pick(row);
    const by = weigh(row);
    if (value === null || value === undefined || Number.isNaN(value)) return;
    if (!by) return;
    weighted += value * by;
    weight += by;
  });

  return weight ? weighted / weight : null;
};
